import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, X, RotateCcw, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function CameraCapture({ onCapture, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const handleOpen = async () => {
    setIsOpen(true);
    setCapturedImage(null);
    setError(null);
    await startCamera();
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setIsOpen(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageDataUrl);
      stopCamera();
    }
  };

  const retake = async () => {
    setCapturedImage(null);
    await startCamera();
  };

  const confirmCapture = async () => {
    if (capturedImage && onCapture) {
      setIsCapturing(true);
      try {
        // Convert base64 to blob
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        
        // Create a file from blob
        const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
        
        await onCapture(file);
        handleClose();
      } catch (err) {
        console.error('Upload error:', err);
        setError("Erreur lors de l'envoi de l'image.");
      } finally {
        setIsCapturing(false);
      }
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={handleOpen}
        disabled={disabled}
        className="hover:bg-blue-50 hover:border-blue-500"
        title="Prendre une photo"
        data-testid="camera-btn"
      >
        <Camera className="w-5 h-5" />
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Prendre une photo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Camera Preview / Captured Image */}
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {capturedImage ? (
                <img 
                  src={capturedImage} 
                  alt="Captured" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              )}
              
              {!capturedImage && !stream && !error && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Instructions */}
            <p className="text-sm text-gray-600 text-center">
              📄 Photographiez votre CV, carte d'identité, diplôme ou certificat
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {capturedImage ? (
                <>
                  <Button
                    variant="outline"
                    onClick={retake}
                    className="flex-1"
                    disabled={isCapturing}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reprendre
                  </Button>
                  <Button
                    onClick={confirmCapture}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                    disabled={isCapturing}
                  >
                    {isCapturing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Utiliser cette photo</>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                  <Button
                    onClick={capturePhoto}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                    disabled={!stream}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Prendre la photo
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
